import { Clock, UtensilsCrossed, Receipt, User, Printer } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { OpenConsumption } from "@/hooks/useOpenConsumption";

interface OpenConsumptionDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consumption: OpenConsumption | null;
}

export function OpenConsumptionDetailsDialog({
  open,
  onOpenChange,
  consumption,
}: OpenConsumptionDetailsDialogProps) {
  if (!consumption) return null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const timeOpen = formatDistanceToNow(new Date(consumption.openedAt), {
    locale: ptBR,
    addSuffix: false,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {consumption.type === "table" ? (
              <UtensilsCrossed className="h-5 w-5 text-primary" />
            ) : (
              <Receipt className="h-5 w-5 text-primary" />
            )}
            <DialogTitle>
              {consumption.type === "table"
                ? `Mesa ${consumption.number}`
                : `Comanda ${consumption.number}`}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {consumption.customerName && (
                <p className="text-sm text-muted-foreground">
                  {consumption.customerName}
                </p>
              )}
              {consumption.ordersCount > 1 && (
                <Badge variant="outline" className="text-xs">
                  {consumption.ordersCount} pedidos
                </Badge>
              )}
            </div>
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {timeOpen}
            </Badge>
          </div>

          <Separator />

          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {consumption.items.map((item) => (
              <div
                key={item.id}
                className="flex items-start gap-3 py-2 px-2.5 rounded-lg bg-muted/50 border border-border"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {item.quantity}x {item.product_name}
                    </span>
                    <span className="text-sm text-foreground ml-auto shrink-0">
                      {formatCurrency(item.total_price)}
                    </span>
                  </div>
                  {item.notes && (
                    <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {item.created_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(item.created_at), "HH:mm:ss")}
                      </span>
                    )}
                    {item.operator_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {item.operator_name}
                      </span>
                    )}
                    {item.printed_at && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Printer className="h-3 w-3" />
                        {format(new Date(item.printed_at), "HH:mm:ss")}
                      </span>
                    )}
                    {!item.printed_at && item.created_at && (
                      <span className="text-orange-500 text-[10px]">Não impresso</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex justify-between font-bold text-lg">
            <span>Subtotal</span>
            <span>{formatCurrency(consumption.subtotal)}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
